"use client";

/* eslint-disable no-nested-ternary */
import { VariantProps, cva } from "class-variance-authority";
import { CheckCircleIcon, DeleteCircleIcon } from "icons";
import React from "react";
import { Label, TextArea } from "react-aria-components";
import { UseFormRegisterReturn } from "react-hook-form";

import { Text } from "../Text/Text";

export interface ITextAreaFieldProps
  extends VariantProps<typeof textFieldWrapperClass> {
  testId?: string;
  label?: string;
  placeholder?: string;
  rows?: number;
  errorMessage?: string;
  helperMessage?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  hasValue?: boolean;
  value?: string;
  onChangeText?: (text: string) => void;
  register?: UseFormRegisterReturn<string>;
  showValidationIcon?: boolean;
}

export function TextAreaField({
  testId = "textArea-test-id",
  label,
  placeholder,
  rows = 5,
  isDisabled = false,
  isRequired = false,
  hasValue = false,
  register,
  errorMessage = "",
  helperMessage = "",
  value,
  onChangeText,
  intent,
  showValidationIcon = true,
}: ITextAreaFieldProps): JSX.Element {
  let calculatedIntent: "primary" | "success" | "warning" | "danger";

  if (intent) {
    calculatedIntent = intent;
  } else if (errorMessage) {
    calculatedIntent = "danger";
  } else if (hasValue) {
    calculatedIntent = "success";
  } else {
    calculatedIntent = "primary";
  }

  return (
    <div className="flex flex-col gap-1 flex-1">
      <Label>
        <Text variant="small" align="left">
          {label}
          {isRequired && (
            <Text
              variant="small"
              intent="secondary"
              className="ml-1"
              align="left"
            >
              (required)
            </Text>
          )}
        </Text>
        <div className={textFieldWrapperClass({ intent: calculatedIntent })}>
          <TextArea
            data-testid={testId}
            disabled={isDisabled}
            className={textFieldClass()}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChangeText?.(e.target.value)}
            rows={rows}
            {...register}
          />
          {calculatedIntent === "success" && showValidationIcon && (
            <CheckCircleIcon className={iconsClass({ intent: "success" })} />
          )}
          {calculatedIntent === "danger" && showValidationIcon && (
            <DeleteCircleIcon className={iconsClass({ intent: "danger" })} />
          )}
        </div>
      </Label>

      {errorMessage ? (
        <Text variant="small" intent={calculatedIntent} align="left">
          {errorMessage}
        </Text>
      ) : (
        <Text variant="small" intent={calculatedIntent} align="left">
          {helperMessage}
        </Text>
      )}
    </div>
  );
}

const textFieldWrapperClass = cva(
  "flex items-center border rounded-md hover:border-primary focus-within:border-primary relative",
  {
    variants: {
      intent: {
        primary: "border-background-dark",
        success: "border-background-dark",
        warning: "border-warning",
        danger: "border-danger",
      },
    },
    defaultVariants: {
      intent: "primary",
    },
  }
);

const textFieldClass = cva(
  "rounded-md outline-none p-2 text-sm font-medium border-none flex-1"
);

const iconsClass = cva(
  "absolute w-4 h-4 right-4  flex items-center pointer-events-none",
  {
    variants: {
      intent: {
        primary: "text-gray-400 ",
        success: "text-success",
        warning: "text-warning",
        danger: "text-danger",
      },
    },
    defaultVariants: {
      intent: "primary",
    },
  }
);
